import { inject } from '@angular/core';
import { signalStore, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Events, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { mapResponse } from '@ngrx/operators';
import { switchMap } from 'rxjs';
import { HallOfFameStats, hallOfFameEvents, PREDICTION_GAME_SERVICE } from '@kikkz/shared/prediction-game/util';
import { TRANSLATION_SERVICE } from '@kikkz/shared/i18n/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import { asyncReducers } from '@kikkz/shared/state/util';
import { serverErrorMessage } from './server-error-message';

export interface HallOfFameState {
  stats: HallOfFameStats | null;
  isLoadingHallOfFame: boolean;
  hallOfFameError: string | null;
}

const initialState: HallOfFameState = {
  stats: null,
  isLoadingHallOfFame: false,
  hallOfFameError: null,
};

/** Hall of Fame region: the all-time records of the prediction game. */
export const HallOfFameStore = signalStore(
  withState(initialState),
  withDevtools('prediction-game-hall-of-fame'),
  withReducer(
    ...asyncReducers<HallOfFameState>()({
      load: hallOfFameEvents.loadHallOfFame,
      success: hallOfFameEvents.loadHallOfFameSuccess,
      failed: hallOfFameEvents.loadHallOfFameFailed,
      loading: 'isLoadingHallOfFame',
      error: 'hallOfFameError',
      onSuccess: (state, payload) => {
        state.stats = payload.stats;
      },
    }),
  ),
  withEventHandlers(
    (
      _store,
      events = inject(Events),
      service = inject(PREDICTION_GAME_SERVICE),
      translation = inject(TRANSLATION_SERVICE),
      logger = inject(LOGGING_SERVICE),
    ) => ({
      loadHallOfFame$: events.on(hallOfFameEvents.loadHallOfFame).pipe(
        switchMap(() =>
          service.loadHallOfFameStats().pipe(
            mapResponse({
              next: (stats) => hallOfFameEvents.loadHallOfFameSuccess({ stats }),
              error: (error: unknown) =>
                hallOfFameEvents.loadHallOfFameFailed({
                  error: serverErrorMessage(error, 'pg.errors.hallOfFameLoad', translation, logger),
                }),
            }),
          ),
        ),
      ),
    }),
  ),
);
